import {
  ApplicationConfig, importProvidersFrom, inject, isDevMode, provideAppInitializer, provideCheckNoChangesConfig,
  provideZonelessChangeDetection
} from '@angular/core';
import {HttpBackend, HttpClient, provideHttpClient, withInterceptors, withXhr} from '@angular/common/http';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {provideToastr} from 'ngx-toastr';
import {provideStore} from '@ngrx/store';
import {provideEffects} from '@ngrx/effects';
import {provideStoreDevtools} from '@ngrx/store-devtools';
import {FaIconLibrary} from '@fortawesome/angular-fontawesome';
// FontAwesome Regular SVG Icons
import {
  faSquare,
  faCheckCircle,
  faTimesCircle,
  faDotCircle,
  faThumbsUp,
  faComments,
  faFolderOpen,
  faTrashAlt,
  faFileImage,
  faFileArchive,
  faLifeRing,
  faCommentDots,
  faFolder,
  faKeyboard,
  faCalendarAlt,
  faEnvelope,
  faAddressCard,
  faMap,
  faObjectGroup,
  faImages,
  faUser,
  faLightbulb,
  faGem,
  faClock,
  faUserCircle,
  faQuestionCircle,
  faBuilding,
  faBell,
  faFileExcel,
  faFileAudio,
  faFileVideo,
  faFileWord,
  faFilePdf,
  faFileCode,
  faFileAlt,
  faEye,
  faChartBar
} from '@fortawesome/free-regular-svg-icons';
// FontAwesome Solid SVG Icons
import {
  faChevronRight,
  faSitemap,
  faPrint,
  faMapMarkerAlt,
  faTachometerAlt,
  faAlignCenter,
  faExternalLinkAlt,
  faShareSquare,
  faInfoCircle,
  faSync,
  faQuoteRight,
  faStarHalfAlt,
  faShapes,
  faCarBattery,
  faTable,
  faCubes,
  faPager,
  faCameraRetro,
  faBomb,
  faNetworkWired,
  faBusAlt,
  faBirthdayCake,
  faEyeDropper,
  faUnlockAlt,
  faDownload,
  faAward,
  faPlayCircle,
  faReply,
  faUpload,
  faBars,
  faEllipsisV,
  faSave,
  faSlidersH,
  faCaretRight,
  faChevronUp,
  faPlus,
  faLemon,
  faChevronLeft,
  faTimes,
  faChevronDown,
  faFilm,
  faSearch,
  faEllipsisH,
  faCog,
  faArrowsAltH,
  faPlusCircle,
  faAngleRight,
  faAngleUp,
  faAngleLeft,
  faAngleDown,
  faArrowUp,
  faArrowDown,
  faArrowRight,
  faArrowLeft,
  faStar,
  faSignOutAlt,
  faLink
} from '@fortawesome/free-solid-svg-icons';
// FontAwesome Brand SVG Icons
import {
  faFacebook,
  faTwitter,
  faLinkedin,
  faAngular,
  faVuejs,
  faReact,
  faHtml5,
  faGoogle,
  faInstagram,
  faPinterest,
  faYoutube,
  faDiscord,
  faSlack,
  faDribbble,
  faGithub,
  faWhatsapp
} from '@fortawesome/free-brands-svg-icons';

import {DatePipe} from '@angular/common';
import {provideRouter, withInMemoryScrolling} from '@angular/router';
import {ROOT_ROUTES} from './app.routing';
import {TzDatePipe} from '@app/modules/shared/pipes/tz-date.pipe';
import {ConfigsService} from '@app/services/configs.service';
import {httpAuthInterceptor} from '@app/services/http-intercept.service';
import {sbTableRequestInterceptor} from '@app/_components/unified/sb-table/sb-table-requests';
import {SocketService} from './services/socket.service';
import {appReducers, metaReducers} from '@app/stores/app.reducers';
import {appEffects} from '@app/stores/app.effects';
import {environment} from '@environments/environment';

// Built over HttpBackend, which skips the interceptor chain entirely. The dictionary is a
// static file under /assets: it carries no Authorization header and must not depend on session
// state. Through the normal HttpClient it went through the auth interceptor, which - on any load
// carrying a stale currentUser - queued it behind POST /auth/refresh; when that refresh 401s the
// dictionary request dies, ngx-translate memoizes the failure and never retries, and every
// `| translate` renders its raw key from then on.
export function HttpLoaderFactory(handler: HttpBackend) {
  return new TranslateHttpLoader(new HttpClient(handler));
}

/** The panel's application: what AppModule imported and provided, and its routes. */
export const appConfig: ApplicationConfig = {
  providers: [
    // No zone.js: a view refreshes when a signal it reads changes, markForCheck() runs (the async
    // pipe does it), a template event fires or an input is set.
    provideZonelessChangeDetection(),
    // Dev builds check every view after each change detection, OnPush ones included: a binding
    // that changed without telling Angular (so would not be shown) throws NG0100.
    ...(isDevMode() ? [provideCheckNoChangesConfig({exhaustive: true})] : []),
    provideRouter(ROOT_ROUTES, withInMemoryScrolling({scrollPositionRestoration: 'enabled'})),
    importProvidersFrom(
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: HttpLoaderFactory,
          deps: [HttpBackend]
        }
      }),
    ),
    provideToastr({
      timeOut: 3000,
      positionClass: 'toast-top-center',
      preventDuplicates: true
    }),
    provideStore(appReducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    provideStoreDevtools({
      maxAge: 25, // Retains last 25 states
      logOnly: environment.production, // Restrict extension to log-only mode
      autoPause: true, // Pauses recording actions and state changes when the extension window is not open
      // connectInZone: true
    }),
    provideEffects(appEffects),
    ConfigsService,
    // withXhr(): HttpClientModule pinned the XHR backend (provideHttpClient now defaults to fetch, which
    // reports no upload progress); keep it so requests go out exactly as before. sb-table's comes first:
    // it reports what the auth interceptor's refresh and retry leave failed.
    provideHttpClient(withInterceptors([sbTableRequestInterceptor, httpAuthInterceptor]), withXhr()),
    SocketService,
    // Provided at the root by the shared and campaign modules before they were removed; components inject them.
    TzDatePipe,
    DatePipe,
    provideAppInitializer(() => {
      inject(FaIconLibrary).addIcons(faFacebook, faPrint, faLinkedin, faAlignCenter, faMapMarkerAlt, faTachometerAlt,
        faExternalLinkAlt, faShareSquare, faSitemap, faInfoCircle, faLifeRing, faTwitter, faQuoteRight, faStarHalfAlt,
        faSync, faShapes, faCarBattery, faTable, faCubes, faPager, faAngular, faVuejs, faReact, faHtml5, faCheckCircle,
        faTimesCircle, faBomb, faNetworkWired, faBusAlt, faBirthdayCake, faEyeDropper, faThumbsUp, faCameraRetro, faUnlockAlt,
        faDownload, faUpload, faReply, faGoogle, faFileImage, faFolderOpen, faBars, faTrashAlt, faSave, faPlayCircle, faEllipsisV,
        faEllipsisH, faSlidersH, faFileArchive, faAward, faCaretRight, faInstagram, faPinterest, faYoutube, faDiscord, faSlack, faWhatsapp, faDribbble,
        faGithub, faPlus, faFolder, faTimes, faEnvelope, faAddressCard, faMap, faCalendarAlt, faImages, faFilm, faClock, faSearch,
        faChevronRight, faChevronUp, faChevronLeft, faChevronDown, faLink, faLightbulb, faGem, faCog, faDotCircle, faArrowsAltH, faComments,
        faCommentDots, faKeyboard, faObjectGroup, faUser, faUserCircle, faQuestionCircle, faBuilding, faBell, faFileExcel, faFileAudio,
        faFileVideo, faFileWord, faFilePdf, faFileCode, faFileAlt, faEye, faChartBar, faPlusCircle, faAngleRight, faAngleUp, faAngleLeft,
        faAngleDown, faArrowUp, faArrowDown, faArrowRight, faArrowLeft, faStar, faSignOutAlt, faLemon);
    }),
  ]
};
